// VNEC v9 — pages/chi-phi.js
// Ghi nhận chi phí — Ước tính lũy kế theo loại CP (Trích trước / Accrual)
// Logic theo v8 FormTaoChiPhi:
//   1. User chọn HĐ + Dự án + Kỳ (tháng/năm)
//   2. Hệ thống load ~32 loại CP từ ifrs_rules, group theo ifrs_category
//   3. User nhập "Ước tính lũy kế" cho từng loại CP
//   4. Hệ thống auto query chi_phi approved theo HĐ + loại CP → Đã TT
//   5. Trích trước = max(0, Ước tính - Đã TT)
//   6. Save → INSERT nhiều rows vào ghi_nhan_cp
import { supabase } from '../lib/supabase.js';
import { formatVND, parseVND, attachVNDFormat } from '../lib/forms.js';
import { ok, err } from '../components/toast.js';

const IFRS_GROUPS = {
  Contract_Direct:    'I.A. COGS trực tiếp (IFRS 15.97a-c)',
  Contract_Allocated: 'I.B. COGS phân bổ (IFRS 15.97d)',
  Obtain_Contract:    'II. Chi phí đấu thầu (IFRS 15.91)',
  Operating_SGA:      'III. Chi phí hoạt động SG&A (IAS 1.99)',
  Inventory:          'IV. Tồn kho (IAS 2)',
  Prepaid:            'V. Trả trước / Phân bổ (IAS 38)'
};

// ═══════════════════════════════════════════════════════════
// LIST VIEW — browse ghi_nhan_cp history
// ═══════════════════════════════════════════════════════════
export async function renderChiPhiList() {
  window.__pageInit = initList;
  return `
    <div class="page">
      <div class="page-header">
        <h1>Ghi nhận trích trước</h1>
        <div class="page-actions">
          <input type="search" id="search" placeholder="Tìm HĐ, dự án..." class="input-search">
          <select id="filter-ky" class="input-sm"><option value="">Tất cả kỳ</option></select>
          <a href="#/chi-phi/new" class="btn btn-primary">+ Ghi nhận mới</a>
        </div>
      </div>
      <div class="card">
        <p class="muted small">Form này ghi nhận <b>ước tính lũy kế</b> theo từng loại chi phí (Accrual / IAS 37). Chi phí chính thức được tạo qua <a href="#/pyctt/new">Phiếu Thanh toán</a>.</p>
        <div class="table-wrap">
          <table class="table" id="gn-table">
            <thead><tr>
              <th>Kỳ</th><th>Mã HĐ</th><th>Mã DA</th><th>Loại CP</th>
              <th class="num">Ước tính</th><th class="num">Đã TT</th><th class="num">Trích trước</th>
              <th>Ghi chú</th>
            </tr></thead>
            <tbody><tr><td colspan="8" class="muted center">Đang tải...</td></tr></tbody>
          </table>
        </div>
      </div>
    </div>`;
}

async function initList() {
  // Populate kỳ filter
  const { data: kyData } = await supabase.from('ghi_nhan_cp')
    .select('ky').is('deleted_at', null).order('ky', { ascending: false }).limit(500);
  const uniqueKy = [...new Set((kyData || []).map(r => r.ky))].filter(Boolean);
  const kySel = document.getElementById('filter-ky');
  uniqueKy.forEach(k => {
    const o = document.createElement('option'); o.value = k; o.textContent = k; kySel.appendChild(o);
  });

  const search = document.getElementById('search');
  const tbody = document.querySelector('#gn-table tbody');

  async function query() {
    tbody.innerHTML = '<tr><td colspan="8" class="muted center">Đang tải...</td></tr>';
    let q = supabase.from('ghi_nhan_cp')
      .select('id, ky, ma_hd, ma_du_an, loai_cp, uoc_tinh, da_tt, trich_truoc, ghi_chu')
      .is('deleted_at', null).order('created_at', { ascending: false }).limit(500);
    if (kySel.value) q = q.eq('ky', kySel.value);
    if (search.value) q = q.or(`ma_hd.ilike.%${search.value}%,ma_du_an.ilike.%${search.value}%,loai_cp.ilike.%${search.value}%`);
    const { data, error } = await q;
    if (error) { tbody.innerHTML = `<tr><td colspan="8">Lỗi: ${error.message}</td></tr>`; return; }
    if (!data.length) { tbody.innerHTML = '<tr><td colspan="8" class="muted center">Chưa có ghi nhận nào.</td></tr>'; return; }
    tbody.innerHTML = data.map(r => `
      <tr>
        <td><b>${r.ky || ''}</b></td>
        <td class="small">${r.ma_hd || ''}</td>
        <td class="small">${r.ma_du_an || ''}</td>
        <td class="small">${r.loai_cp || ''}</td>
        <td class="num">${formatVND(r.uoc_tinh)}</td>
        <td class="num muted">${formatVND(r.da_tt)}</td>
        <td class="num" style="color:#854F0B;font-weight:700">${formatVND(r.trich_truoc)}</td>
        <td class="small muted">${r.ghi_chu || ''}</td>
      </tr>`).join('');
  }

  let t;
  search.addEventListener('input', () => { clearTimeout(t); t = setTimeout(query, 300); });
  kySel.addEventListener('change', query);
  query();
}

// ═══════════════════════════════════════════════════════════
// FORM — Ghi nhận trích trước (Accrual)
// ═══════════════════════════════════════════════════════════
export async function renderChiPhiForm(id) {
  window.__pageInit = () => initForm(id);
  return `
    <div class="page">
      <div class="page-header">
        <h1>Ghi nhận trích trước — Ước tính lũy kế</h1>
        <a href="#/chi-phi" class="btn btn-ghost">← Danh sách</a>
      </div>

      <div class="card">
        <h3 class="card-title">Thông tin chung</h3>
        <div class="grid-2">
          <label class="field"><span>Dự án *</span>
            <select id="sel-duan" required><option value="">-- Chọn dự án --</option></select>
          </label>
          <label class="field"><span>Hợp đồng *</span>
            <select id="sel-hd" required><option value="">-- Chọn HĐ --</option></select>
          </label>
          <label class="field"><span>Tháng *</span>
            <select id="sel-thang" required>
              <option value="">--</option>
              ${[1,2,3,4,5,6,7,8,9,10,11,12].map(m => `<option value="${String(m).padStart(2,'0')}">${String(m).padStart(2,'0')}</option>`).join('')}
            </select>
          </label>
          <label class="field"><span>Năm *</span>
            <input id="inp-nam" type="number" min="2020" max="2040" value="${new Date().getFullYear()}" required>
          </label>
        </div>
        <label class="field"><span>Ghi chú</span>
          <input id="inp-ghichu" placeholder="Ghi chú cho kỳ này">
        </label>
      </div>

      <div class="card" id="summary-card" style="display:none">
        <h3 class="card-title">Tổng hợp kỳ</h3>
        <div class="summary-row" style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
          <div class="summary-card sc-est" style="background:#E6F1FB;border-left:3px solid #185FA5;border-radius:8px;padding:14px;text-align:center">
            <div style="font-size:20px;font-weight:800;color:#000066" id="sc-est">0</div>
            <div style="font-size:10px;color:#666;margin-top:4px">ƯỚC TÍNH LŨY KẾ</div>
          </div>
          <div class="summary-card sc-paid" style="background:#E8F5EE;border-left:3px solid #1B6B3A;border-radius:8px;padding:14px;text-align:center">
            <div style="font-size:20px;font-weight:800;color:#1B6B3A" id="sc-paid">0</div>
            <div style="font-size:10px;color:#666;margin-top:4px">ĐÃ THANH TOÁN (HSTT)</div>
          </div>
          <div class="summary-card sc-acr" style="background:#FFF8E1;border-left:3px solid #EF9F27;border-radius:8px;padding:14px;text-align:center">
            <div style="font-size:20px;font-weight:800;color:#854F0B" id="sc-acr">0</div>
            <div style="font-size:10px;color:#666;margin-top:4px">TRÍCH TRƯỚC (ACCRUAL)</div>
          </div>
        </div>
      </div>

      <div class="card" id="table-card" style="display:none">
        <h3 class="card-title">Bảng lũy kế loại chi phí IFRS <span class="muted small" id="hd-label" style="float:right"></span></h3>
        <div class="table-wrap">
          <table class="table table-compact" id="cp-table">
            <thead><tr>
              <th style="width:40px">#</th>
              <th>Loại chi phí</th>
              <th class="num" style="width:180px">Ước tính lũy kế</th>
              <th class="num" style="width:160px">Đã TT (tự tính)</th>
              <th class="num" style="width:160px">Trích trước</th>
            </tr></thead>
            <tbody id="cp-tbody"></tbody>
            <tfoot><tr style="background:var(--navy,#000066);color:var(--gold,#FFCC00);font-weight:800">
              <td colspan="2" style="padding:10px">TỔNG</td>
              <td class="num" id="foot-est">0</td>
              <td class="num" id="foot-paid">0</td>
              <td class="num" id="foot-acr">0</td>
            </tr></tfoot>
          </table>
        </div>
      </div>

      <div class="card" id="empty-card">
        <p class="muted center" style="padding:40px">Chọn <b>Dự án</b>, <b>Hợp đồng</b> và <b>Kỳ</b> để load bảng ghi nhận.</p>
      </div>

      <div class="form-actions">
        <a href="#/chi-phi" class="btn btn-ghost">Hủy</a>
        <button type="button" class="btn btn-primary" id="btn-save" disabled>Lưu trích trước (Draft)</button>
      </div>
    </div>`;
}

let _state = {
  ifrsRules: [],    // [{loai_cp, default_phan_loai, locked}, ...]
  actualMap: {},    // { loai_cp: sum_tong_tt }
  editId: null
};

async function initForm(id) {
  _state.editId = id || null;
  const selDA = document.getElementById('sel-duan');
  const selHD = document.getElementById('sel-hd');
  const selThang = document.getElementById('sel-thang');
  const inpNam = document.getElementById('inp-nam');
  const inpGhiChu = document.getElementById('inp-ghichu');
  const btnSave = document.getElementById('btn-save');

  // Load catalogs
  const [daRes, hdRes, rulesRes] = await Promise.all([
    supabase.from('du_an').select('ma_da, ten_da').eq('trang_thai','active').is('deleted_at',null).order('ma_da'),
    supabase.from('hop_dong').select('so_hd, ma_hd, nha_thau, ma_du_an').eq('trang_thai','approved').is('deleted_at',null).limit(2000),
    supabase.from('ifrs_rules').select('loai_cp, default_phan_loai, locked').order('loai_cp')
  ]);

  (daRes.data || []).forEach(d =>
    selDA.insertAdjacentHTML('beforeend', `<option value="${d.ma_da}">${d.ma_da} — ${d.ten_da}</option>`));

  // v3: Auto-fill + lock project cho data_entry
  try {
    const { getCurrentProject } = await import('../lib/session.js');
    const { getCurrentUser } = await import('../lib/supabase.js');
    const u = await getCurrentUser();
    const curProj = await getCurrentProject();
    if (curProj) {
      selDA.value = curProj;
      if (u.role === 'data_entry' || u.role === 'viewer') {
        selDA.disabled = true;
        selDA.style.background = '#EEEDFE';
        selDA.title = 'Dự án khóa theo phiên làm việc';
      }
      // Auto-trigger change để filter HĐ
      populateHDSelect(curProj);
    }
  } catch (e) { /* silent */ }

  const allHD = (hdRes.data || []).map(r => ({
    ma_hd: r.ma_hd || r.so_hd,
    nha_thau: r.nha_thau,
    ma_du_an: r.ma_du_an
  }));

  function populateHDSelect(filterDA) {
    selHD.innerHTML = '<option value="">-- Chọn HĐ --</option>';
    const list = filterDA ? allHD.filter(h => h.ma_du_an === filterDA) : allHD;
    list.forEach(h => {
      selHD.insertAdjacentHTML('beforeend', `<option value="${h.ma_hd}">${h.ma_hd} — ${h.nha_thau || ''}</option>`);
    });
  }
  populateHDSelect('');

  _state.ifrsRules = rulesRes.data || [];

  selDA.addEventListener('change', () => { populateHDSelect(selDA.value); selHD.value = ''; tryLoadTable(); });
  selHD.addEventListener('change', tryLoadTable);
  selThang.addEventListener('change', tryLoadTable);
  inpNam.addEventListener('change', tryLoadTable);

  async function tryLoadTable() {
    if (!selHD.value || !selThang.value || !inpNam.value || !selDA.value) return;
    const maHD = selHD.value;
    document.getElementById('hd-label').textContent = maHD;

    // Query chi_phi approved by this HĐ, SUM tong_tt group by loai_cp
    const { data: cpData, error: cpErr } = await supabase.from('chi_phi')
      .select('loai_cp, tong_tt').eq('ma_hd', maHD).eq('trang_thai', 'approved').is('deleted_at', null);
    if (cpErr) { err('Lỗi query chi phí: ' + cpErr.message); return; }
    _state.actualMap = {};
    (cpData || []).forEach(r => {
      _state.actualMap[r.loai_cp] = (_state.actualMap[r.loai_cp] || 0) + Number(r.tong_tt || 0);
    });

    // If edit mode, load existing ghi_nhan_cp rows for this (ma_hd, ky)
    const ky = selThang.value + '/' + inpNam.value;
    let existingMap = {};
    const { data: gnData } = await supabase.from('ghi_nhan_cp')
      .select('id, loai_cp, uoc_tinh, ghi_chu').eq('ma_hd', maHD).eq('ky', ky).is('deleted_at', null);
    (gnData || []).forEach(r => { existingMap[r.loai_cp] = r; });

    renderCPTable(existingMap);
    document.getElementById('empty-card').style.display = 'none';
    document.getElementById('summary-card').style.display = '';
    document.getElementById('table-card').style.display = '';
    btnSave.disabled = false;
    calc();
  }

  function renderCPTable(existingMap) {
    const tbody = document.getElementById('cp-tbody');
    tbody.innerHTML = '';
    let idx = 0, lastGrp = '';
    _state.ifrsRules.forEach(rule => {
      const lcp = rule.loai_cp;
      const cat = rule.default_phan_loai || 'Contract_Direct';
      const grp = IFRS_GROUPS[cat] || 'Khác';
      if (grp !== lastGrp) {
        const trG = document.createElement('tr');
        trG.className = 'grp-row';
        trG.innerHTML = `<td colspan="5" style="background:#ECEDFE;color:#000066;font-weight:800;padding:8px 12px">${grp}</td>`;
        tbody.appendChild(trG);
        lastGrp = grp;
      }
      idx++;
      const daTT = _state.actualMap[lcp] || 0;
      const existing = existingMap[lcp];
      const uocTinh = existing ? Number(existing.uoc_tinh) : 0;

      const tr = document.createElement('tr');
      tr.dataset.loaiCp = lcp;
      if (existing) tr.dataset.existingId = existing.id;
      tr.innerHTML = `
        <td class="num small">${idx}</td>
        <td><b>${lcp}</b></td>
        <td><input name="luyke" inputmode="numeric" value="${uocTinh > 0 ? formatVND(uocTinh) : ''}" style="text-align:right"></td>
        <td class="num muted">${formatVND(daTT)}</td>
        <td class="num" name="acr-display" style="color:#854F0B;font-weight:700">0</td>`;
      tbody.appendChild(tr);

      const inp = tr.querySelector('[name=luyke]');
      attachVNDFormat(inp);
      inp.addEventListener('input', calc);
    });
  }

  function calc() {
    let tEst = 0, tPaid = 0, tAcr = 0;
    document.querySelectorAll('#cp-tbody tr[data-loai-cp]').forEach(tr => {
      const lcp = tr.dataset.loaiCp;
      const inp = tr.querySelector('[name=luyke]');
      const est = parseVND(inp.value) || 0;
      const paid = _state.actualMap[lcp] || 0;
      const acr = Math.max(0, est - paid);
      const cell = tr.querySelector('[name=acr-display]');
      if (est === 0) cell.textContent = '0';
      else if (acr === 0) { cell.textContent = '✓ Khớp'; cell.style.color = '#1B6B3A'; }
      else { cell.textContent = formatVND(acr); cell.style.color = '#854F0B'; }
      tEst += est; tPaid += paid; tAcr += acr;
    });
    document.getElementById('sc-est').textContent = formatVND(tEst);
    document.getElementById('sc-paid').textContent = formatVND(tPaid);
    document.getElementById('sc-acr').textContent = formatVND(tAcr);
    document.getElementById('foot-est').textContent = formatVND(tEst);
    document.getElementById('foot-paid').textContent = formatVND(tPaid);
    document.getElementById('foot-acr').textContent = formatVND(tAcr);
  }

  btnSave.addEventListener('click', async () => {
    const maHD = selHD.value;
    const maDuAn = selDA.value;
    const ky = selThang.value + '/' + inpNam.value;
    const ghiChu = inpGhiChu.value || null;

    if (!maHD || !maDuAn || !selThang.value || !inpNam.value) {
      err('Vui lòng điền đủ thông tin chung'); return;
    }

    // Collect rows with uoc_tinh > 0
    const rows = [];
    document.querySelectorAll('#cp-tbody tr[data-loai-cp]').forEach(tr => {
      const lcp = tr.dataset.loaiCp;
      const est = parseVND(tr.querySelector('[name=luyke]').value) || 0;
      const paid = _state.actualMap[lcp] || 0;
      if (est > 0) {
        rows.push({
          ky, ma_hd: maHD, ma_du_an: maDuAn, loai_cp: lcp,
          noi_dung: 'Ước tính lũy kế ' + lcp + ' đến ' + ky,
          uoc_tinh: est, da_tt: paid,
          ghi_chu: ghiChu
          // trich_truoc: GENERATED (uoc_tinh - da_tt)
          // created_by: auto-filled by trigger
        });
      }
    });

    if (!rows.length) { err('Chưa nhập ước tính cho loại CP nào'); return; }

    if (!confirm(`Ghi nhận trích trước cho ${rows.length} loại CP tại kỳ ${ky}?`)) return;

    btnSave.disabled = true; btnSave.textContent = 'Đang lưu...';
    try {
      // Delete existing rows for same (ma_hd, ky) trước khi insert mới — replace pattern
      await supabase.from('ghi_nhan_cp').delete().eq('ma_hd', maHD).eq('ky', ky).is('deleted_at', null);

      const { error: iErr } = await supabase.from('ghi_nhan_cp').insert(rows);
      if (iErr) throw iErr;

      ok(`Đã lưu ${rows.length} ghi nhận trích trước cho kỳ ${ky}`);
      setTimeout(() => { window.location.hash = '#/chi-phi'; }, 600);
    } catch (e) {
      err('Lỗi: ' + e.message);
      btnSave.disabled = false;
      btnSave.textContent = 'Lưu trích trước (Draft)';
    }
  });
}
